import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, ArrowRight, Palette, Eye, Heart } from "lucide-react";

const C = {
  bg: "#070707", bg2: "#11100E", card: "#181614", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", goldBr: "#E0BC72", red: "#FF3B30",
  bSub: "rgba(245,242,235,0.10)", bGold: "rgba(201,160,85,0.35)",
};
const F = { brush: "'Permanent Marker', cursive", cond: "'Saira Condensed', sans-serif", hand: "'Caveat', cursive", sans: "'DM Sans', 'Outfit', sans-serif" };

function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    obs.observe(ref.current);
    const timer = setTimeout(() => setV(true), 600);
    return () => { obs.disconnect(); clearTimeout(timer); };
  }, [threshold]);
  return [ref, v];
}

const MarkerStroke = ({ color = C.gold, width = "100%", height = "6px", className = "" }) => (
  <div className={className} style={{ width, height, background: color, borderRadius: "3px", transform: "rotate(-0.8deg)", opacity: 0.85, boxShadow: `0 0 12px ${color}40` }} />
);

export default function MCGallery() {
  useSEO({ title: "Gallery — Marker Collection", description: "Browse the full Marker Collection. Original artwork and high-quality prints, hand-drawn mark by mark." });
  const navigate = useNavigate();
  const [artworks, setArtworks] = useState([]);
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState("ALL");
  const [sortBy, setSortBy] = useState("default");
  const [heroRef, heroVis] = useVisible(0.1);

  // Force hero visible after data loads (observer may miss if ref attaches late)
  const [show, setShow] = useState(false);
  useEffect(() => { if (!loading) { const t = setTimeout(() => setShow(true), 100); return () => clearTimeout(t); } }, [loading]);

  useEffect(() => {
    Promise.all([
      http.get("/marker-collection/public/products"),
      http.get("/marker-collection/public/pages"),
    ]).then(([a, p]) => {
      setArtworks((a.data || []).filter(x => !x.linked_parent_id));
      setPageInfo(p.data?.gallery || {});
      if (p.data?.gallery?.enabled === false) navigate("/marker-collection");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [navigate]);

  const allTags = useMemo(() => {
    const s = new Set();
    artworks.forEach(a => { if (a.art_type) s.add(a.art_type); (a.tags || []).forEach(t => s.add(t)); });
    return ["ALL", ...s];
  }, [artworks]);

  const filtered = activeTag === "ALL" ? artworks : artworks.filter(a => a.art_type === activeTag || (a.tags || []).includes(activeTag));

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortBy === "price-low") arr.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === "price-high") arr.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sortBy === "originals") arr.sort((a, b) => (a.art_type === "Original" ? 0 : 1) - (b.art_type === "Original" ? 0 : 1));
    return arr;
  }, [filtered, sortBy]);

  if (loading) return (
    <Layout><div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <div className="w-16 h-16 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
    </div></Layout>
  );

  return (
    <Layout>
      <div style={{ background: C.bg, color: C.text, minHeight: "100vh" }}>
        <style>{`
          .mc-card-dramatic { transition: all 0.4s cubic-bezier(0.25,0.46,0.45,0.94); }
          .mc-card-dramatic:hover { transform: translateY(-8px) scale(1.02); }
          .mc-card-dramatic:hover .mc-card-overlay { opacity: 1; }
          .mc-card-dramatic:hover .mc-card-image { transform: scale(1.08); }
          .mc-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>

        {/* Hero */}
        <section ref={heroRef} className="relative mc-grit overflow-hidden" style={{ background: C.bg, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-gallery-hero">
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <Link to="/marker-collection" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold mb-6 hover:opacity-70 transition-opacity"
              style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-gallery-back">
              <ArrowLeft size={14} /> Back to Home
            </Link>
            <div className={`transition-all duration-700 ${show || heroVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="text-[10px] tracking-[0.4em] uppercase font-bold mb-3" style={{ fontFamily: F.sans, color: C.red }}>ORIGINAL ARTWORK & PRINTS</p>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.85] tracking-tight mb-4" style={{ fontFamily: F.cond, color: C.text }} data-testid="mc-gallery-title">
                {pageInfo?.heading || "THE FULL COLLECTION"}
              </h1>
              <MarkerStroke width="80px" className="mb-4" />
              <p className="text-base max-w-lg" style={{ fontFamily: F.sans, color: C.text2 }}>
                {pageInfo?.subheading || "Every piece, one place. Browse, filter, and find the one that speaks to you."}
              </p>
            </div>
          </div>
        </section>

        {/* Filters + Grid */}
        <section className="relative mc-grit" style={{ background: C.bg2 }} data-testid="mc-gallery-grid-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-16 relative z-10">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-6 sm:mb-8" data-testid="mc-gallery-controls">
              <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap w-full sm:w-auto no-scrollbar">
                {allTags.map(tag => {
                  const count = tag === "ALL" ? artworks.length : artworks.filter(a => a.art_type === tag || (a.tags || []).includes(tag)).length;
                  return (
                    <button key={tag} onClick={() => setActiveTag(tag)}
                      className="flex items-center gap-1.5 px-3 sm:px-5 py-2 text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.2em] uppercase font-bold transition-all duration-300 flex-shrink-0"
                      style={{ fontFamily: F.sans, borderRadius: "999px", border: `2px solid ${activeTag === tag ? C.gold : C.bSub}`, background: activeTag === tag ? C.gold : "transparent", color: activeTag === tag ? C.bg : C.text2 }}
                      data-testid={`mc-gallery-tag-${tag}`}>
                      {tag} <span className="text-[7px] sm:text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: activeTag === tag ? "rgba(0,0,0,0.2)" : `${C.gold}15`, color: activeTag === tag ? C.bg : C.gold }}>{count}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-[10px] tracking-[0.15em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>{sorted.length} piece{sorted.length !== 1 ? "s" : ""}</span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  className="text-[10px] tracking-[0.12em] uppercase px-3 py-2 outline-none cursor-pointer"
                  style={{ fontFamily: F.sans, background: "rgba(255,255,255,0.05)", border: `1px solid ${C.bSub}`, color: C.text2, appearance: "auto" }}
                  data-testid="mc-gallery-sort">
                  <option value="default">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="originals">Originals First</option>
                </select>
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5 lg:gap-8" data-testid="mc-gallery-product-grid">
              {sorted.map((art, i) => <GalleryCard key={art.id} art={art} index={i} />)}
              {sorted.length === 0 && (
                <div className="col-span-full text-center py-16">
                  <Palette size={40} className="mx-auto mb-4" style={{ color: `${C.gold}30` }} />
                  <p style={{ fontFamily: F.sans, color: C.text2 }}>No artworks found for "{activeTag}"</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function GalleryCard({ art, index }) {
  const isSold = art.original_status === "sold" || (art.quantity != null && art.quantity <= 0);
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!cardRef.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.08, rootMargin: "0px 0px -40px 0px" });
    obs.observe(cardRef.current);
    return () => obs.disconnect();
  }, []);

  // Stagger within each row (2 cols mobile, 3 lg, 4 xl) — use index % 4 for consistent feel
  const stagger = (index % 4) * 90;

  return (
    <Link ref={cardRef} to={`/marker-collection/${art.slug}`} className="mc-card-dramatic block relative group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(32px) scale(0.97)",
        transition: `opacity 0.55s cubic-bezier(0.25,0.46,0.45,0.94) ${stagger}ms, transform 0.55s cubic-bezier(0.25,0.46,0.45,0.94) ${stagger}ms`,
      }}
      data-testid={`mc-gallery-card-${art.slug}`}>
      <div className="relative overflow-hidden" style={{ background: C.card, border: `1px solid ${C.bSub}` }}>
        <div className="overflow-hidden">
          {art.main_image ? (
            <img src={resolveImage(art.main_image)} alt={art.title} className="mc-card-image w-full aspect-[3/4] object-cover transition-transform duration-700" loading="lazy" />
          ) : (
            <div className="w-full aspect-[3/4] flex items-center justify-center" style={{ background: C.bg2 }}><Palette size={32} style={{ color: `${C.gold}20` }} /></div>
          )}
        </div>
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 px-2 sm:px-3 py-0.5 sm:py-1 text-[7px] sm:text-[8px] tracking-[0.3em] uppercase font-bold z-10"
          style={{ fontFamily: F.sans, background: art.art_type === "Original" ? C.red : C.gold, color: art.art_type === "Original" ? "#fff" : C.bg }}>
          {art.art_type || "ART"}
        </div>
        {art.charity?.enabled && (
          <div className="absolute top-2 sm:top-3 right-2 sm:right-3 z-10 flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 sm:py-1"
            style={{ background: "rgba(0,0,0,0.7)", border: "1px solid rgba(74,222,128,0.3)", backdropFilter: "blur(6px)" }}>
            <Heart size={8} fill="#4ade80" color="#4ade80" />
            <span className="text-[6px] sm:text-[8px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: F.sans, color: "#4ade80" }}>CAUSE</span>
          </div>
        )}
        <div className="mc-card-overlay absolute inset-0 opacity-0 transition-opacity duration-400 hidden sm:flex flex-col items-center justify-end p-5"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)" }}>
          <div className="flex items-center gap-3 px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold" style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}>
            <Eye size={14} /> VIEW PIECE
          </div>
        </div>
        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center z-20" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(2px)" }}>
            <span className="text-sm tracking-[0.35em] uppercase font-black" style={{ fontFamily: F.cond, color: C.text }}>SOLD</span>
          </div>
        )}
        <div className="sm:hidden absolute bottom-2 right-2 px-2 py-1 z-10" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(4px)" }}>
          <span className="text-sm font-black" style={{ fontFamily: F.cond, color: isSold ? C.text2 : C.gold }}>${Number(art.price || 0).toFixed(0)}</span>
        </div>
      </div>
      <div className="mt-1.5 sm:mt-3 flex items-end justify-between gap-1 sm:gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] sm:text-base font-bold truncate leading-tight" style={{ fontFamily: F.sans, color: C.text }}>{art.title}</p>
        </div>
        <span className="hidden sm:block text-xl font-black flex-shrink-0" style={{ fontFamily: F.cond, color: isSold ? C.text2 : C.gold }}>${Number(art.price || 0).toFixed(0)}</span>
      </div>
    </Link>
  );
}
